import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Ban, RotateCcw, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Avatar, VerifiedBadge } from "@/components/Brand";
import { api, formatApiError } from "@/lib/api";

const DURATIONS = [["24h", "24 horas"], ["7d", "7 dias"], ["30d", "30 dias"], ["indef", "Tempo indeterminado"]];

function SuspendDialog({ u, onClose, onDone }) {
  const [duration, setDuration] = useState("7d");
  const [reason, setReason] = useState("");
  const go = async () => {
    try {
      const { data } = await api.post(`/super/users/${u.id}/suspend`, { duration, reason });
      toast.success(`${u.name} suspenso(a)`, { description: data.email_sent ? "Aviso enviado por e-mail." : "Não foi possível enviar o aviso por e-mail." });
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="suspend-dialog">
        <DialogHeader><DialogTitle>Suspender {u.name}</DialogTitle><DialogDescription>A conta não consegue entrar, as publicações saem do feed e as lives são encerradas. O usuário recebe um aviso por e-mail com o prazo e o motivo.</DialogDescription></DialogHeader>
        <Select value={duration} onValueChange={setDuration}>
          <SelectTrigger data-testid="suspend-duration-select"><SelectValue /></SelectTrigger>
          <SelectContent>{DURATIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Motivo (opcional)" data-testid="suspend-reason-input" />
        <DialogFooter><Button onClick={go} className="rounded-full bg-amber-500 font-bold text-black hover:bg-amber-400" data-testid="suspend-confirm-button">Suspender</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ u, onClose, onDone }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.delete(`/super/users/${u.id}`); toast.success("Conta excluída permanentemente"); onDone(); } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-user-dialog">
        <DialogHeader><DialogTitle>Excluir {u.name} permanentemente?</DialogTitle>
          <DialogDescription>Apaga a conta, as publicações, os comentários, as salas, os documentos e os arquivos na nuvem. Não é possível desfazer.</DialogDescription></DialogHeader>
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder='Digite "EXCLUIR" para confirmar' data-testid="delete-user-confirm-input" />
        <DialogFooter><Button onClick={go} disabled={txt !== "EXCLUIR" || busy} className="rounded-full bg-red-600 font-bold hover:bg-red-500" data-testid="delete-user-confirm-button">{busy ? <Loader2 className="animate-spin" size={16} /> : "Excluir para sempre"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, onSuspend, onDelete, onReactivate }) {
  const until = u.suspended_forever ? "indeterminado" : u.suspended_until ? new Date(u.suspended_until).toLocaleString("pt-BR") : "";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/5 bg-card p-3" data-testid={`super-user-${u.id}`}>
      <Avatar name={u.name} src={u.avatar_url || u.logo_url} size={40} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate font-bold">{u.name}{u.verified && <VerifiedBadge size={14} />}</p>
        <p className="break-words text-xs text-muted-foreground">{u.email} · {u.role === "institution" ? "Instituição" : u.is_minor ? "Usuário (menor)" : "Usuário"} · {u.posts} posts{u.city ? ` · ${u.city}/${u.state}` : ""}</p>
        {u.suspended && <p className="mt-0.5 text-xs font-semibold text-amber-300" data-testid={`super-user-suspended-${u.id}`}>Suspenso até {until}{u.suspend_reason ? ` — ${u.suspend_reason}` : ""}</p>}
      </div>
      <div className="flex w-full justify-end gap-2 sm:w-auto">
        {u.suspended
          ? <Button size="sm" variant="secondary" className="rounded-full" onClick={() => onReactivate(u)} data-testid={`super-user-reactivate-${u.id}`}><RotateCcw size={14} className="mr-1" />Reativar</Button>
          : <Button size="sm" variant="secondary" className="rounded-full" onClick={() => onSuspend(u)} data-testid={`super-user-suspend-${u.id}`}><Ban size={14} className="mr-1" />Suspender</Button>}
        <Button size="sm" variant="secondary" className="rounded-full hover:bg-red-600" onClick={() => onDelete(u)} data-testid={`super-user-delete-${u.id}`}><Trash2 size={14} /></Button>
      </div>
    </div>
  );
}

export function UsersPanel() {
  const [f, setF] = useState({ q: "", role: "all", status: "all" });
  const [data, setData] = useState(null);
  const [suspend, setSuspend] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/super/users", { params: f }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const reactivate = async (u) => {
    try {
      const { data } = await api.post(`/super/users/${u.id}/reactivate`);
      toast.success("Conta reativada", { description: data.email_sent ? "Aviso enviado por e-mail." : "Não foi possível enviar o aviso por e-mail." });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const done = () => { setSuspend(null); setDel(null); load(); };

  return (
    <div className="space-y-4" data-testid="super-users-panel">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Buscar por nome ou e-mail" className="h-10 rounded-full bg-white/5 pl-9" data-testid="super-users-search" /></div>
        <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="h-10 w-40 rounded-full" data-testid="super-users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="user">Usuários</SelectItem><SelectItem value="institution">Instituições</SelectItem></SelectContent></Select>
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="h-10 w-40 rounded-full" data-testid="super-users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Qualquer status</SelectItem><SelectItem value="suspended">Suspensos</SelectItem></SelectContent></Select>
      </div>
      {data && <p className="text-xs text-muted-foreground" data-testid="super-users-total">{data.total} conta(s)</p>}
      <div className="space-y-2">{data?.items.map((u) => <UserRow key={u.id} u={u} onSuspend={setSuspend} onDelete={setDel} onReactivate={reactivate} />)}</div>
      {suspend && <SuspendDialog u={suspend} onClose={() => setSuspend(null)} onDone={done} />}
      {del && <DeleteDialog u={del} onClose={() => setDel(null)} onDone={done} />}
    </div>
  );
}
